import { useTheme } from "@emotion/react";
import { ensureDate, formatShortDateOnly } from "../../other/dateTime/format";
import { Sanitize } from "../../other/sanitize/Sanitize";
import { useInternalMediaLink } from "./useInternalMediaLink";
import { locationPrepend } from "../../other/devServer/locationPrepend";

type InternalMediaDto =
    RedDeer.InternalMedia.Client.UI.Contracts.Dtos.InternalMediaDto;

export function InternalMediaListItem({ item }: { item: InternalMediaDto }) {
    const theme = useTheme();
    const isDark = theme.name === "dark";
    const { href, onClick, target } = useInternalMediaLink(item.id);

    return (
        <div
            className={`relative w-full rounded-xl border p-6 shadow-sm transition ${
                isDark
                    ? "border-slate-600 bg-slate-800 hover:border-slate-500"
                    : "border-slate-200 bg-white hover:border-slate-300"
            }`}
        >
            {item.id && (
                <a
                    href={href}
                    onClick={onClick}
                    target={target}
                    className="absolute inset-0 z-10 rounded-xl"
                    aria-label={
                        item.title ? `Read ${item.title}` : "Read article"
                    }
                />
            )}
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:gap-6">
                {item.thumbnailImage && (
                    <img
                        src={`${locationPrepend()}/InternalMedia/Resource/${item.thumbnailImage}`}
                        alt=""
                        className="h-40 w-full rounded-lg object-cover lg:h-32 lg:w-48 lg:flex-shrink-0"
                    />
                )}
                <div className="min-w-0 flex-grow">
                    <div
                        className={`mb-2 text-xl font-semibold ${isDark ? "text-white" : "text-slate-900"}`}
                    >
                        {item.title}
                    </div>
                    <div className="mb-3 flex flex-wrap items-center gap-3">
                        {item.author && (
                            <span
                                className={`text-sm ${isDark ? "text-slate-300" : "text-slate-600"}`}
                            >
                                {item.author}
                            </span>
                        )}
                        <span
                            className={`text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}
                        >
                            {formatShortDateOnly(
                                ensureDate(item.publishedDateTime)
                            )}
                        </span>
                    </div>
                    <div
                        className={`text-sm leading-relaxed ${isDark ? "text-slate-300" : "text-slate-600"}`}
                    >
                        <Sanitize content={item.previewDescription ?? ""} />
                    </div>
                </div>
            </div>
        </div>
    );
}
